import {
  AppType,
  defaultReviewSortType,
  Label,
  LocalizedSeoTag,
  ReviewSortType,
  reviewSortTypeToPathParam,
  SeoTag,
} from '@web-catalog/types';

import {entityToPathSegment} from '@web-catalog/utils/entityToPathSegment';

type PageProps = {
  page?: number;
  category?: string;
  query?: string;
};

const getPage = (index: string, {page, category, query}: PageProps = {}) => {
  let path = index;

  if (category) {
    path += `${category}/`;
  }

  if (page && page > 1) {
    path += `page-${page}`;
  }

  if (query && page) {
    path += `?query=${query}`;
  }

  return path;
};

export const ROUTES = {
  main: () => 'https://www.rustore.ru',
  root: {
    index: () => '/',
    page: (pageProps?: PageProps) => getPage(ROUTES.root.index(), pageProps),
  },
  app: {
    app: (packageName?: string) =>
      packageName ? `/app/${packageName}` : '/app/:packageName',
  },
  search: {
    index: (query?: Record<string, string>) =>
      `/search${query ? `?${new URLSearchParams(query).toString()}` : ''}`,
    page: (pageProps?: PageProps) =>
      getPage(`${ROUTES.search.index()}/`, pageProps),
  },
  developer: {
    developer: (devId?: string) =>
      devId ? `/developer/${devId}` : '/developer/:devId',
  },
  recommendation: {
    recommendation: (packageName?: string) =>
      packageName
        ? `/app/${packageName}/recommendation`
        : '/app/:packageName/recommendation',
  },
  reviews: {
    reviews: (
      packageName?: string,
      pageNumber?: number,
      sortBy?: ReviewSortType,
    ) => {
      if (!packageName) {
        return '/app/:packageName/ReviewItems';
      }

      const parts = [`/app/${packageName}/reviews`];

      if (sortBy && sortBy !== defaultReviewSortType) {
        parts.push(reviewSortTypeToPathParam[sortBy]);
      }

      if (pageNumber && pageNumber > 1) {
        parts.push(`page-${pageNumber}`);
      }

      return parts.join('/');
    },
  },
  versions: {
    versions: (packageName?: string, pageNumber?: number) =>
      packageName
        ? `/app/${packageName}/versions${
            pageNumber && pageNumber > 1 ? '/page-' + pageNumber : ''
          }`
        : '/app/:packageName/versions',
  },
  games: {
    index: () => '/games',
    page: (pageProps?: PageProps) =>
      getPage(`${ROUTES.games.index()}/`, pageProps),
  },
  featuring: {
    featuring: (featuringId?: string) => `/featuring/${featuringId}`,
  },
  restrictions: {
    restrictions: (packageName?: string) =>
      packageName
        ? `/app/${packageName}/restrictions`
        : '/app/:packageName/restrictions',
  },
  seoTag: {
    index: (tag: LocalizedSeoTag | SeoTag, pageNumber?: number) => {
      const basePath = `/tag/${entityToPathSegment(tag.id, 'localizedLabel' in tag ? tag.localizedLabel || tag.defaultLabel : tag.label)}`;
      return [
        basePath,
        typeof pageNumber === 'number' && pageNumber > 1
          ? `page-${pageNumber}`
          : '',
      ]
        .filter(Boolean)
        .join('/');
    },
  },
  label: {
    index: (label: Label, pageNumber?: number) => {
      const basePath = `/label/${entityToPathSegment(label.id, label.name)}`;
      return [
        basePath,
        typeof pageNumber === 'number' && pageNumber > 1
          ? `page-${pageNumber}`
          : '',
      ]
        .filter(Boolean)
        .join('/');
    },
  },
  category: {
    index: (appType: AppType, categoryName: string) =>
      appType === AppType.GAMES
        ? `/games/${categoryName.toLowerCase()}`
        : `/${categoryName.toLowerCase()}`,
  },
  event: {
    index: (id: number | string) => `/event/${id}`,
  },
  kiosk: {
    index: () => '/kiosk',
  },
  tv: {
    index: () => '/tv',
  },
};
